import type { MouseEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey } from '@connectrpc/connect-query'
import { listBilateralTrades } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_service-SupplyBilateralTradeService_connectquery'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  Box,
  HStack,
  ListPager,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CompactAlert } from '~/components/common/CompactAlert'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { CsvImportAppliedAlert } from '~/components/common/CsvUpload/CsvImportAppliedAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { useCsvImportLink } from '~/hooks/useCsvImportReturn'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { useGetWorkflowCase } from '~/hooks/workflowCase/useGetWorkflowCase'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { formatDateDisplay, fromProtoDate } from '~/models/dateTime'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'
import { FilterBar } from '~/pages/SupplyBilateral/components/FilterBar'
import { ManualTradeDetailModal } from '~/pages/SupplyBilateral/components/ManualTradeDetailModal'
import { RecapModal } from '~/pages/SupplyBilateral/components/RecapModal'
import { TradeTable } from '~/pages/SupplyBilateral/components/TradeTable'
import { useDeleteBilateralTrade } from '~/pages/SupplyBilateral/hooks/useDeleteBilateralTrade'
import { useFilterState } from '~/pages/SupplyBilateral/hooks/useFilterState'
import { useGetBilateralTrade } from '~/pages/SupplyBilateral/hooks/useGetBilateralTrade'
import { useGetBilateralTradeRecap } from '~/pages/SupplyBilateral/hooks/useGetBilateralTradeRecap'
import { useListBilateralTrades } from '~/pages/SupplyBilateral/hooks/useListBilateralTrades'
import {
  decimalString,
  formatLastImportedAt,
} from '~/pages/SupplyBilateral/models/formatters'
import { buildSupply48Url } from '~/pages/SupplyBilateral/models/supply48Link'
import { routePaths } from '~/routes/routePaths'

type Props = {
  /** ロール識別子。権限制御に使用。省略時は read-only 扱い。 */
  userRole?: string
}

type DeleteTarget = {
  id: string
  publicTradeId: string
  updatedAt: Timestamp | undefined
}

type RecapTarget = {
  id: string
}

type DetailTarget = {
  id: string
}

/**
 * 相対取引一覧ページ。
 *
 * 機能スコープ:
 *   - 8 種フィルタ AND state (初期=すべて/初期化)。 主要フィルタは常時表示、
 *     詳細フィルタ (受渡区分/スプレッド/時間帯/経路/相対先) は「詳細フィルター」
 *     トグルで開閉 (= プロトタイプ準拠、 サマリーカードは廃止)
 *   - テーブル (約定日降順→取引ID昇順, 売買色, 受渡区分視覚区別, 手入力行視覚区別)
 *   - 取引契約一覧 card (header + 一覧 + footer を 1 つの角丸枠に統合)。 card が
 *     残り高さを満たし一覧だけ内部スクロール、 footer (番号付きページャ) は card
 *     下端に固定。 card-header 右端に CSV アップロードボタン (= 唯一の書き込み導線)
 *   - 買/売/純の集計 MW は出さない。 契約ごとに受渡期間・時間帯区分が違う MW の単純
 *     合計で業務上の意味を持たないため footer から撤去した (#6813 / #6847)。 サマリー
 *     カード (AC-SBL-005 / 017〜020) も欠番 (#6855)
 *   - ローディング/0件/eSL失敗エラー (AC-006/014/017)
 *   - ページネーション (番号付き ‹ 1 2 › / N-M/全X件 AC-015)
 *
 * 月別グループ化はデザイン非採用のため廃止 (= フラット一覧に一本化)。
 *
 * P0 スコープ (#4801 / #1574、 spec `docs/specs/supply/bilateral-trade-list.md`):
 *   - L453 決定事項「Phase 1: eBalance 側で CSV 一括アップロードのみ提供 (画面上の
 *     個別手動登録・編集・削除 UI は提供しない、 編集は CSV 再アップロードで対応)」
 *   - L468 読み取り専用「取引一覧テーブルの全行で編集・削除ボタンが表示されないこと」
 *   → 個別入力モーダルの起点・行操作列の編集/削除ボタンは非表示。 閲覧導線
 *     (Recap / 詳細 / 48点データへ) はそのまま。 削除ハンドラ・削除確認ダイアログ・
 *     楽観ロック (expected_updated_at proto Timestamp 直渡し) は P1 の再有効化に
 *     備えて配線ごと残置している (起点ボタンが無いため到達しない)。
 *
 * 権限制御:
 *   - Recap / 閲覧は全ロールで可能 (P0 では編集・削除導線が無いためロール差分なし)
 *   - TODO(#4801): P1 で個別入力 UI を戻す際に「powerProcurement のみ編集/削除
 *     enabled」の権限制御を復活させる (判定ロジックは TradeRow に残置)
 *
 * Feature 導線 (TODO):
 *   - TODO(#846): Recap モーダルは Feature B で実装済み。追加改善は #846 で対応
 */
export const SupplyBilateralPage = ({
  userRole,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  // 取り込んだらこの画面 (今の絞り込みのまま) へ戻る
  const csvImportLink = useCsvImportLink(
    routePaths.adminUploadsBilateralTrade.path,
  )
  // workflow-back-bar-readonly AC-02/AC-19: caseId は URL クエリパラメータ
  // `?workflowId=` から取得し、read-only 制御 (完了済み
  // ケースで取引作成・編集・削除・インポートを無効化) に使う (fail-closed の理由は
  // useCurrentWorkflowCase の JSDoc 参照)。
  const { caseId, wfCase, isReadonly, isDateLocked } = useCurrentWorkflowCase()
  const { readonlyReason } = useWorkflowLockReasons()
  // 受渡期間の初期値に使う対象日が決まるまで一覧を取得しない。ケースの取得が
  // 失敗した時と対象日を持たないケースでは、対象日なしの初期値で取得を始める
  // (受渡期間は固定しないため、AC-WFW-006a Decision 1 の fail-closed で止め続けず
  // 通常の初期値へ倒す)。cache は useCurrentWorkflowCase と共有するので重複取得しない。
  const { isPending: isWfCasePending } = useGetWorkflowCase(caseId, {
    throwOnError: false,
  })
  const isInitialPeriodReady = !isDateLocked || !isWfCasePending
  // AC-SBL-022 の ※: ワークフロー起点では受渡期間の初期値を対象日〜対象日にする。
  // この画面は受渡期間で絞り込むため対象日を固定しない (AC-WFW-006a の例外)。
  const { filter, setFilter, resetFilter } = useFilterState({
    workflowTargetDate: wfCase?.targetDate,
    isWorkflowLinked: isDateLocked,
  })

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | undefined>(
    undefined,
  )
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined)
  const [recapTarget, setRecapTarget] = useState<RecapTarget | undefined>(
    undefined,
  )
  // 手入力取引閲覧モーダル (Feature D) の対象。 設定時のみ GetBilateralTrade を fetch。
  const [detailTarget, setDetailTarget] = useState<DetailTarget | undefined>(
    undefined,
  )
  // エリアフィルタの選択肢を絞る業務エリア設定 (AC-SBL-023 / #609)。
  // 取得経路は Supply48Data と同じ GetPersonalSettings。 未取得時は空配列のまま
  // FilterBar へ渡す (= fallback せず「すべて」のみ。 理由は FilterBar 側の JSDoc)。
  const { data: personalSettings } = useGetPersonalSettingsQuery()
  const businessAreaCodes = useMemo(
    () => personalSettings?.settings?.businessAreas ?? [],
    [personalSettings],
  )
  // 設定更新で選択中エリアが業務エリア外になったら絞り込みを「すべて」へ戻す。
  // 選択肢から消えたエリアの絞り込みが残ると、 選べないエリアの取引を取得し続け
  // 一覧と選択肢が不整合になる (TC-SBL-R-01「選択肢と表示データ範囲が新設定に整合」)。
  // 正規化は Supply48Data の useSupply48Filters と同型 (未取得 = 空配列のときは
  // 正規化しない。 取得前の一時的な空でユーザーの選択を消さないため)。
  useEffect(() => {
    if (
      businessAreaCodes.length > 0 &&
      filter.areaCodeFilter !== undefined &&
      !businessAreaCodes.includes(filter.areaCodeFilter)
    ) {
      setFilter({ areaCodeFilter: undefined })
    }
  }, [businessAreaCodes, filter.areaCodeFilter, setFilter])

  const {
    data,
    isLoading: isListLoading,
    error: listError,
  } = useListBilateralTrades({
    buySellFilter: filter.buySellFilter,
    executionRouteFilter: filter.executionRouteFilter,
    timeZoneTypeFilter: filter.timeZoneTypeFilter,
    deliveryTypeFilter: filter.deliveryTypeFilter,
    areaCodeFilter: filter.areaCodeFilter,
    partnerNameFilter: filter.partnerNameFilter,
    deliveryPeriodFrom: filter.deliveryPeriodFrom,
    deliveryPeriodTo: filter.deliveryPeriodTo,
    statusFilter: filter.statusFilter,
    spreadKindFilter: filter.spreadKindFilter,
    includeExpired: filter.includeExpired,
    page: filter.page,
    pageSize: filter.pageSize,
    // 対象日が届く前に取得すると、受渡期間を絞らない一覧が一瞬出る。
    enabled: isInitialPeriodReady,
  })
  // 取得を止めている間 (対象日の取得待ち) も読み込み中として扱う。
  const isLoading = isListLoading || !isInitialPeriodReady

  const { mutateAsync: deleteAsync, isPending: isDeleting } =
    useDeleteBilateralTrade()

  // Recap 詳細 fetch (= recapTarget が設定された時のみ実行)
  const {
    data: recapData,
    isLoading: isRecapLoading,
    error: recapError,
  } = useGetBilateralTradeRecap(recapTarget?.id)
  const recapDetail = recapData?.recap

  // 手入力取引閲覧モーダルの full 情報 fetch (= detailTarget 設定時のみ)。
  const {
    data: detailData,
    isLoading: isDetailLoading,
    error: detailError,
  } = useGetBilateralTrade(detailTarget?.id ?? '')
  const tradeDetail = detailData?.trade
  // AC-SBM-007: 受渡期間セクションのカレンダービュー用 (backend 計算済み)。
  const tradeDetailCalendarDays = detailData?.calendarDays

  // ---------------------------------------------------------------------------
  // 取り込み途絶の警告バナー (AC-SBL-016)
  //
  // stale=true の時だけ出す。最終取込は JST。lastImportedAt 未設定 (途絶えている
  // 連携元に一度も成功していないものを含む) は「—」。連携元名は出さない。
  // 日付形式は ja が YYYY/MM/DD HH:mm、en が YYYY-MM-DD HH:mm (spec 英語付録)。
  // ---------------------------------------------------------------------------
  const importStaleness = data?.importStaleness
  const importStaleTitle = importStaleness?.stale
    ? t('page:supplyBilateral.importStaleTitle', {
        lastImportedAt: formatLastImportedAt(
          importStaleness.lastImportedAt,
          i18n.language.startsWith('en'),
        ),
      })
    : undefined

  // ---------------------------------------------------------------------------
  // ページネーション計算
  // ---------------------------------------------------------------------------
  const totalCount = data?.totalCount ?? 0
  const pageSize = filter.pageSize
  const page = filter.page
  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, totalCount)
  const paginationLabel =
    totalCount === 0 ? '' : `${startItem}-${endItem} / 全${totalCount}件`
  // ListPager (DS) 用の総ページ数。
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  // DS `ListPager` の前後 chevron は icon のみで accessible name を持たない
  // (`ListPagerProps` に label を渡す口が無い)。 そのため pager root の
  // 最初 / 最後の button (= chevron。 DS の構成は chevron・番号…・chevron 固定) に
  // aria-label と title を DOM で付与する。 DS `Tooltip` は内部 button へ外から
  // 挿せないため、 `ButtonTooltip` の JSDoc と同じ整理で **title を保険**にする
  // (本筋は DS 側で label prop を持つこと = eclair-designsystem への課題)。
  // 内部 button は page 変更で作り直されるため、 mount 時 (ref) でなく
  // effect で毎回貼り直す。
  const pagerRootsRef = useRef<Array<HTMLDivElement | null>>([null, null])
  useEffect(() => {
    for (const root of pagerRootsRef.current) {
      if (root === null) continue
      const buttons = root.querySelectorAll('button')
      if (buttons.length < 2) continue
      const prev = buttons[0]
      const next = buttons[buttons.length - 1]
      const prevLabel = t('page:supplyBilateral.pagination.prev')
      const nextLabel = t('page:supplyBilateral.pagination.next')
      prev.setAttribute('aria-label', prevLabel)
      prev.setAttribute('title', prevLabel)
      next.setAttribute('aria-label', nextLabel)
      next.setAttribute('title', nextLabel)
    }
  })
  // ---------------------------------------------------------------------------
  // ハンドラー
  // ---------------------------------------------------------------------------
  const handleRecap = (id: string): void => {
    // TODO(#846): Recap モーダルは Feature B で実装済み。追加改善は #846 で対応
    setRecapTarget({ id })
  }

  // 手入力取引の「詳細」→ 手入力取引閲覧モーダル (Feature D、 read-only) を開く。
  const handleViewDetail = (id: string): void => {
    setDetailTarget({ id })
  }

  // 「48点データへ」→ 該当取引のコンテキストをクエリパラメータで引き渡して遷移
  // (AC-010 / AC-012)。 list item から URL を組み立てる (受け側は date / filterArea /
  // buysell を初期フィルタに反映する)。
  const handleGoTo48 = (id: string, _e?: MouseEvent): void => {
    const item = data?.items.find((i) => i.id === id)
    if (item === undefined) {
      return
    }
    // [挙動] 常に別タブで開く (2026-08-31 デザイナー指摘「48点データを見る
    // ボタンをクリック後、別タブで 48 点データ画面を表示」)。一覧の文脈 (この
    // 画面のフィルタ・スクロール位置) を保ったまま対象契約の 48 点を参照する
    // 使い方のため。タブ名は counterparty query を useDocumentTitle が拾う。
    // 2026-10-16 のデザイナー決定 (別途指定の無いリンクは別タブで開かない) の下でも、
    // 日付と理由の記録がある Des の決定による別タブ指定として別タブのまま残す。
    window.open(
      buildSupply48Url({
        areaCode: item.areaCode,
        buySell: item.buySell,
        deliveryPeriodFrom: item.deliveryPeriodFrom,
        publicTradeId: item.publicTradeId,
        partnerName: item.partnerName,
        volumeMw: decimalString(item.volumeMw),
        caseId,
      }),
      '_blank',
      'noopener,noreferrer',
    )
  }

  // TODO(#4801): 個別入力 (新規登録) / 編集の起点ハンドラは P0 非表示化に伴い撤去した。
  //   P1 再有効化時に handleNewEntry / handleEdit と editTarget state を戻す。
  const handleDeleteRequest = (
    id: string,
    updatedAt: Timestamp | undefined,
  ): void => {
    const item = data?.items.find((i) => i.id === id)
    if (item !== undefined) {
      setDeleteTarget({
        id,
        publicTradeId: item.publicTradeId,
        updatedAt,
      })
    }
  }

  const handleDeleteConfirm = async (): Promise<void> => {
    if (deleteTarget === undefined) {
      return
    }
    try {
      await deleteAsync({
        id: deleteTarget.id,
        expectedUpdatedAt: deleteTarget.updatedAt,
      })
      setDeleteTarget(undefined)
      setDeleteError(undefined)
    } catch (err) {
      // ダイアログを閉じてからエラーバナーを表示する。
      // Chakra UI Modal が開いているとき、main content に aria-hidden が付き
      // エラーバナー (role="alert") が getByRole で見つからなくなるため。
      setDeleteTarget(undefined)
      // ConnectError の Code に応じて i18n message を分岐:
      //   - Aborted (= optimistic lock conflict): 「一覧を再読み込みしてください」 と案内
      //     これを怠ると user が同じ stale timestamp で retry し続けて永久ループに陥る。
      //   - NotFound: 「既に削除されている可能性があります」 と案内
      //   - その他: generic な unknown message
      if (err instanceof ConnectError) {
        switch (err.code) {
          case Code.Aborted:
            setDeleteError(t('page:supplyBilateral.serverError.aborted'))
            return
          case Code.NotFound:
            setDeleteError(t('page:supplyBilateral.serverError.notFound'))
            return
          default:
            break
        }
      }
      setDeleteError(t('page:supplyBilateral.serverError.unknown'))
    }
  }

  const handleDeleteCancel = (): void => {
    setDeleteTarget(undefined)
    setDeleteError(undefined)
  }

  const handleResetFilter = (): void => {
    resetFilter()
    // フィルタ初期化後に list query を強制 invalidate して refetch する。
    // resetFilter() でフィルタが既に同じ値の場合、 query key が変わらず
    // TanStack Query が自動 refetch しないため、 明示的に invalidate が必要。
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listBilateralTrades,
        cardinality: 'finite',
      }),
    })
  }

  const breadcrumbItems = [
    { label: t('page:supplyBilateral.breadcrumb.supply') },
    {
      label: t('page:supplyBilateral.breadcrumb.tradingContract'),
      bold: true,
    },
  ]

  // ---------------------------------------------------------------------------
  // レンダリング
  // ---------------------------------------------------------------------------
  return (
    <WorkflowPageLayout
      // ワークフローから開いた人に、この画面でやることを伝える (2026-10-07 デザイナー指摘)。
      // この画面に来るのは雛形「供給力確認」の「その他供給力確認」 (相対取引・自社電源の供給力を
      // 確認・積み上げる)。受渡期間は対象日で絞って開く (AC-SBL-022 の ※) ので、対象日を文言に入れる。
      // この画面には完了トリガーが無い (AC-WFW-007f の対象外) ため、完了は業務ワークフロー側で行う
      backBarGuide={(c) =>
        c.targetDate !== undefined
          ? t('page:supplyBilateral.workflowGuide', {
              date: formatDateDisplay(fromProtoDate(c.targetDate)),
            })
          : t('page:supplyBilateral.workflowGuideNoDate')
      }
      breadcrumbItems={breadcrumbItems}
      notices={
        <>
          {/* 取り込み途絶の警告バナー (AC-SBL-016) */}
          {importStaleTitle !== undefined && (
            <CompactAlert
              description={t('page:supplyBilateral.importStaleDescription')}
              role="alert"
              status="warning"
              title={importStaleTitle}
            />
          )}

          {/* 削除エラーバナー */}
          {deleteError !== undefined && (
            <CompactAlert role="alert" status="error" title={deleteError} />
          )}

          {/* CSV を取り込んで戻った時の結果 (1 回だけ)。 警告・エラーより優先度が低いので下に置く */}
          <CsvImportAppliedAlert kind="bilateralTrade" />
        </>
      }
    >
      <PageBody>
        {/* フィルタバー */}
        <FilterBar
          availableAreaCodes={businessAreaCodes}
          counterpartyOptions={data?.counterpartyOptions ?? []}
          filter={filter}
          onFilterChange={(partial) => {
            setFilter(partial)
          }}
          onReset={handleResetFilter}
          resultCount={data?.totalCount}
        />

        {/* ローディング */}
        {isLoading && <RegionLoading isLoading />}

        {/* メインコンテンツ: 取引契約一覧 (header + 一覧 + footer) を 1 つの角丸 card に
            まとめる (= プロトタイプ準拠)。 スクロール領域は共通 PageScrollArea が所有し、
            card 自体は flex:1 + minHeight:0 + 縦 flex で残り高さいっぱいに広げ、
            card-body だけ内部スクロールさせて header / footer を固定する
            (= PageScrollArea の JSDoc が想定する「flex:1 の子が自身のスクロール境界になる」ケース)。 */}
        {!isLoading && (data !== undefined || listError !== null) && (
          <PageScrollArea>
            <Box
              flexDirection="column"
              flex={1}
              display="flex"
              overflow="hidden"
              minHeight={0}
              // ① 背景・枠なしの直置き (2026-08-31 デザイナー指定)。 それまでの
              // 8080 `.card` 準拠 (白地 + 枠 + radius lg + shadow) から方針変更し、
              // テーブルをページ地に直接置く。 design (bilateral.html) 側は後追いで更新する。
            >
              {/* card-header: タイトル + 月別グループ化 + 全展開 / 全折りたたみ。
                design `.card-header` (components.css:529-539):
                  - background 無指定 = 親 `.card` の --surface-primary (白) を継承する
                    (以前の Surface.secondary(グレー) は乖離だったため、
                    親と同じ Surface.primary を明示して修正)
                  - padding: var(--space-4) (= 16px 全周。 以前の
                    paddingX=4/paddingY=2 (16px/8px) は乖離だったため統一) */}
              <HStack
                align="center"
                justifyContent="space-between"
                paddingY={4}
              >
                {/* 取引契約一覧 見出し。 design supply/bilateral.html:371 の
                  .card-header は components.css:399-404 (lg(16px)/semibold) に
                  対応するため textStyle="sectionTitle" に統一する
                  (= 意図的な視覚変更 14px/700→16px/600、 ユーザー承認済み)。 */}
                <Text textStyle="sectionTitle">
                  {t('page:supplyBilateral.tableHeader')}
                </Text>
                {/* CSV アップロード (= card-header 右端)。
                  spec `bilateral-trade-list.md` L453 決定事項
                  「Phase 1: CSV 一括アップロードのみ提供 (個別手動登録・編集・削除 UI は
                  提供しない)」。遷移先は相対取引契約の個別アップロード画面。
                  CSV ダウンロードは出力 RPC 未実装のため導線なし (TODO #6025)。
                  variant の SoT は design HTML。 `design/supply/bilateral.html` の操作帯コメント
                  「**variant は btn-tertiary 固定** (ユーザー指示)。 P2 OFF で「追加」が
                  消えて唯一の作成導線になった場合も primary へ格上げしない」 に従い
                  TertiaryButton。 spec L168 は 「プライマリーボタン」と書いていて
                  食い違うが、 visual concern は design が正のため Tertiary を採用する。
                  TODO(#4801): P1 で個別入力 UI を再有効化する際は、 ここに
                  ＋追加 dropdown (個別入力 / 取引データをインポート の DS Menu) を戻す
                  (i18n key `toolbar.individualEntry` は残置済み。「追加」の key は戻す時に足す)。 */}
                {/* ②③ 右端クラスタ: [CSVアップロード] 16px [ListPager] (2026-08-31
                  デザイナー指定: CSV は右端・上側 pager の左 16px)。 pager はテーブルの
                  上下に同じものを置く (下側は footer 行)。 */}
                <HStack gap={4}>
                  <ButtonTooltip content={readonlyReason}>
                    <TertiaryButton
                      disabled={isReadonly}
                      onClick={() => {
                        void navigate(csvImportLink)
                      }}
                      size="md"
                    >
                      {t('page:supplyBilateral.toolbar.csvUpload')}
                    </TertiaryButton>
                  </ButtonTooltip>
                  {listError === null && (
                    <ListPager
                      ref={(el: HTMLDivElement | null) => {
                        pagerRootsRef.current[0] = el
                      }}
                      current={page}
                      goto={(n) => setFilter({ page: n })}
                      pages={totalPages}
                    />
                  )}
                </HStack>
              </HStack>

              {/* テーブル本体 (= 縦スクロールする領域)。 flex:1 + minHeight:0 +
                overflow:auto で高さ内に収め、 上下の pager 行を固定したまま
                一覧だけスクロールさせる。

                器は需要想定一覧 (MasterDataTable の wrap) と同じ
                「白地 + 枠 1px Border.lowEmphasis + 角丸 md」 にする
                (2026-08-31 デザイナー指摘「需要想定一覧画面同様、 table に
                radius つき border」)。 直置き化 (①) はページ全体のカードを
                やめる話で、 テーブル自体の枠はこの指摘で復元された。
                横スクロールはここでは持たない: TradeTable 内側の wrap が
                overflowX を持ち、 sticky 操作列の影の測定基準を兼ねている
                (TradeTable の JSDoc 参照。 二重の scroll container にしない)。 */}
              <Box
                flex={1}
                overflowY="auto"
                minHeight={0}
                background="Surface.primary"
                borderWidth="1px"
                borderColor="Border.lowEmphasis"
                borderRadius="md"
              >
                {listError !== null ? (
                  <Text
                    padding={8}
                    color="Text.highEmphasis"
                    textAlign="center"
                    role="alert"
                  >
                    {t('page:supplyBilateral.listError')}
                  </Text>
                ) : (
                  data !== undefined && (
                    <TradeTable
                      isReadonly={isReadonly}
                      // 一覧の行表現は「スプレッド = 2 行 / 非定型 = 1 行」 (設計書 §5)。
                      // #6236 (Design Decision 1) 以前は backend が 1 行 = 1 leg で返す
                      // ケースの畳み込みが必要だったが、#6235 で 1 契約 = 1 行が保証され
                      // 畳み込み (collapseNonStandardLegRows) は no-op のため撤去済み。
                      items={data.items}
                      // TODO(#4801): onEdit は P0 で起点ごと非表示化 (spec L468)。
                      //   削除ハンドラ (onDelete) は P1 再有効化に備えて配線のまま残す。
                      onDelete={handleDeleteRequest}
                      onGoTo48={handleGoTo48}
                      onRecap={handleRecap}
                      onViewDetail={handleViewDetail}
                      userRole={userRole}
                    />
                  )
                )}
              </Box>

              {/* 下側 pager (③)。 ① で card を解体したためここは「下端固定バー」では
                なくなり、 8/27 の bar 統一 (primary 地 + 上罫線) はその前提ごと消えた。
                範囲ラベルは従来どおり左端に残す。 */}
              {listError === null && (
                <HStack
                  align="center"
                  justifyContent="space-between"
                  paddingY={3}
                >
                  <Text color="Text.highEmphasis" fontSize="sm">
                    {paginationLabel}
                  </Text>
                  <ListPager
                    ref={(el: HTMLDivElement | null) => {
                      pagerRootsRef.current[1] = el
                    }}
                    current={page}
                    goto={(n) => setFilter({ page: n })}
                    pages={totalPages}
                  />
                </HStack>
              )}
            </Box>
          </PageScrollArea>
        )}

        {/* 削除確認ダイアログ */}
        <ConfirmDialog
          body={t('page:supplyBilateral.delete.confirmBody', {
            publicTradeId: deleteTarget?.publicTradeId ?? '',
          })}
          confirmLabel={t('page:supplyBilateral.delete.confirmLabel')}
          destructive
          isLoading={isDeleting}
          isOpen={deleteTarget !== undefined}
          onCancel={handleDeleteCancel}
          onConfirm={() => void handleDeleteConfirm()}
          title={t('page:supplyBilateral.delete.confirmTitle')}
        />

        {/* Recap モーダル (Feature B 実装): loading/error/data を内部で出し分ける。
            3 分岐 (loading Modal / RecapModal / RecapErrorModal) を 1 つに統合。
            TODO(#846): 追加改善は #846 で対応 */}
        <RecapModal
          isError={recapError !== null && recapError !== undefined}
          isLoading={isRecapLoading}
          isOpen={recapTarget !== undefined}
          onClose={() => setRecapTarget(undefined)}
          recap={recapDetail}
        />

        {/* 手入力取引閲覧モーダル (Feature D、 read-only)。 loading/error/data を
            内部で出し分ける。 footer の「48点データを見る」 は handleGoTo48 に委譲。 */}
        <ManualTradeDetailModal
          calendarDays={tradeDetailCalendarDays}
          isError={detailError !== null && detailError !== undefined}
          isLoading={isDetailLoading}
          isOpen={detailTarget !== undefined}
          onClose={() => setDetailTarget(undefined)}
          onGoTo48={
            detailTarget !== undefined
              ? (e?: MouseEvent) => handleGoTo48(detailTarget.id, e)
              : undefined
          }
          trade={tradeDetail}
        />

        {/* TODO(#4801): 個別入力モーダル (新規登録 / 編集) の起点は P0 で非表示化した
            (spec L453 決定事項 / L468 読み取り専用)。 本番から到達できなくなった
            旧 component (`components/TradeFormModal`) と専用の model / hook は削除した。
            P1 で再有効化する時は git 履歴を参考に作り直してここに置き、 card-header と
            行操作列に起点ボタンを復活させる。 */}
      </PageBody>
    </WorkflowPageLayout>
  )
}
